// Peca compartilhada de "batidas de ponto" — nasceu dentro do ComissoesPainel ("Ponto da
// semana") e foi extraida porque agora aparece em 2 telas (Comissoes + Equipe), ver
// feedback_componentizacao_catalogo. Aqui moram: os tipos da sequencia, os helpers de
// formatacao, a linha de UM dia (LinhaDiaPonto) e o bloco de "todas as semanas do mes" com
// "+" aninhado por semana (PontoSemanasDoMes), usado no expand da linha do colaborador.
import { useEffect, useState } from 'react';
import { Plus, Minus, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';

export interface EventoPonto { id: number; tipo: 'entrada' | 'saida'; hora: string; bypass: boolean; auto: boolean }
export interface AnomaliaPonto { tipo: string; descricao: string }
export interface DiaPonto { data: string; dia: number; atrasou: boolean; eventos: EventoPonto[]; anomalias: AnomaliaPonto[] }

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

export const ROTULO_ANOMALIA: Record<string, string> = {
  saida_auto: 'saida automatica',
  pausa_irreal: 'pausa irreal',
  manual: 'anomalia manual',
  chamado_esquecido: 'chamado esquecido',
};

// Nome curto do dia da semana a partir de 'YYYY-MM-DD' (meio-dia UTC pra nao deslocar timezone).
export function diaSemanaCurto(dataIso: string): string {
  return DIAS_SEMANA[new Date(`${dataIso}T12:00:00Z`).getUTCDay()] ?? '';
}

// Sequencia "08:02 → 12:01 · 13:05 → 18:30" a partir das batidas do dia (ja ordenadas).
// Pareia entrada→saida; entrada sem saida vira "(em aberto)"; saida automatica marca (auto).
export function fmtSequenciaPonto(eventos: EventoPonto[]): string {
  const partes: string[] = [];
  for (let k = 0; k < eventos.length; k++) {
    const e = eventos[k]!;
    if (e.tipo === 'entrada') {
      const prox = eventos[k + 1];
      if (prox && prox.tipo === 'saida') {
        partes.push(`${e.hora} → ${prox.hora}${prox.auto ? ' (auto)' : ''}`);
        k++;
      } else {
        partes.push(`${e.hora} → (em aberto)`);
      }
    } else {
      partes.push(`(?) → ${e.hora}${e.auto ? ' (auto)' : ''}`);
    }
  }
  return partes.join('  ·  ');
}

// Quebra um mes nas mesmas semanas do sistema (S1..Sn): cada semana vai ate o proximo sabado
// (ou fim do mes). Mesma regra de gerarSemanasPadrao (ComissoesConfig) pra ponto e comissao
// baterem a mesma semana.
export function gerarSemanasDoMes(ano: number, mes: number): { semana_num: number; dia_inicio: number; dia_fim: number }[] {
  const ultimoDia = new Date(ano, mes, 0).getDate();
  const semanas: { semana_num: number; dia_inicio: number; dia_fim: number }[] = [];
  let semNum = 1;
  let diaInicio = 1;
  while (diaInicio <= ultimoDia) {
    const dow = new Date(ano, mes - 1, diaInicio).getDay(); // 0=Dom, 6=Sab
    const diasAteSabado = (6 - dow + 7) % 7;
    const diaFim = Math.min(diaInicio + diasAteSabado, ultimoDia);
    semanas.push({ semana_num: semNum, dia_inicio: diaInicio, dia_fim: diaFim });
    semNum++;
    diaInicio = diaFim + 1;
  }
  return semanas;
}

// Uma linha de dia: "Seg 22  06:55 → 11:01 · 13:00 → 17:32" + selos de atraso/anomalia/manual.
export function LinhaDiaPonto({ d }: { d: DiaPonto }) {
  const temBypass = d.eventos.some((e) => e.bypass);
  return (
    <div className="flex items-center gap-2 py-0.5 text-xs flex-wrap">
      <span className="w-14 flex-shrink-0 text-gray-500">{diaSemanaCurto(d.data)} {d.dia}</span>
      <span className="font-mono text-gray-700">
        {d.eventos.length > 0 ? fmtSequenciaPonto(d.eventos) : <span className="text-gray-400">sem batida</span>}
      </span>
      {d.atrasou && (
        <span className="px-1.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#fdba74', color: '#000' }}>
          Atrasou
        </span>
      )}
      {d.anomalias.map((a, i) => (
        <span key={i} className="px-1.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#fde68a', color: '#000' }} title={a.descricao}>
          {ROTULO_ANOMALIA[a.tipo] ?? a.tipo}
        </span>
      ))}
      {temBypass && (
        <span className="px-1.5 rounded text-[10px] text-gray-600 border border-gray-300" title="Batida manual (sem reconhecimento facial)">
          manual
        </span>
      )}
    </div>
  );
}

function mesLabel(ano: number, mes: number): string {
  return new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

// Bloco "todas as semanas do mes" pra UM colaborador, dentro do "+" da linha dele na Equipe
// (so admin/gerente — /api/ponto/sequencia exige perfil). Navega mes a mes (default: mes atual)
// e mostra cada semana com seu proprio "+" que expande as batidas dia a dia daquela semana.
export function PontoSemanasDoMes({ usuarioId }: { usuarioId: number }) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [dias, setDias] = useState<DiaPonto[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [semanasAbertas, setSemanasAbertas] = useState<Set<number>>(new Set());

  useEffect(() => {
    let vivo = true;
    setLoading(true); setErro(false); setDias(undefined); setSemanasAbertas(new Set());
    const inicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
    const ultimo = new Date(ano, mes, 0).getDate();
    const fim = `${ano}-${String(mes).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
    api.get<DiaPonto[]>(`/api/ponto/sequencia?usuario_id=${usuarioId}&inicio=${inicio}&fim=${fim}`)
      .then((r) => { if (vivo) setDias(r); })
      .catch(() => { if (vivo) setErro(true); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [usuarioId, ano, mes]);

  function mudarMes(delta: number) {
    let m = mes + delta;
    let a = ano;
    if (m < 1) { m = 12; a--; }
    if (m > 12) { m = 1; a++; }
    setMes(m); setAno(a);
  }

  function toggleSemana(num: number) {
    setSemanasAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num); else next.add(num);
      return next;
    });
  }

  const semanas = gerarSemanasDoMes(ano, mes);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <button type="button" onClick={() => mudarMes(-1)} className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 hover:bg-gray-100" title="Mes anterior">
          <ChevronLeft size={13} />
        </button>
        <span className="font-semibold capitalize min-w-[120px] text-center">{mesLabel(ano, mes)}</span>
        <button type="button" onClick={() => mudarMes(1)} className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 hover:bg-gray-100" title="Proximo mes">
          <ChevronRight size={13} />
        </button>
      </div>

      {loading ? (
        <span className="text-gray-400 text-xs">Carregando ponto...</span>
      ) : erro ? (
        <span className="text-red-500 text-xs">Nao foi possivel carregar o ponto.</span>
      ) : (
        <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
          {semanas.map((s) => {
            const aberta = semanasAbertas.has(s.semana_num);
            const diasSem = (dias ?? []).filter((d) => d.dia >= s.dia_inicio && d.dia <= s.dia_fim);
            const comBatida = diasSem.filter((d) => d.eventos.length > 0).length;
            return (
              <div key={s.semana_num} className="border-b border-gray-100 last:border-0">
                <button
                  type="button"
                  onClick={() => toggleSemana(s.semana_num)}
                  className="w-full flex items-center gap-3 px-3 py-1.5 text-xs text-left hover:bg-gray-50"
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 text-gray-600 flex-shrink-0">
                    {aberta ? <Minus size={11} /> : <Plus size={11} />}
                  </span>
                  <span className="font-medium text-gray-700">Semana {s.semana_num}</span>
                  <span className="text-gray-500">dias {s.dia_inicio}–{s.dia_fim}</span>
                  <span className="ml-auto text-gray-400">
                    {comBatida === 0 ? 'sem batidas' : `${comBatida} dia${comBatida === 1 ? '' : 's'} com batida`}
                  </span>
                </button>
                {aberta && (
                  <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                    {diasSem.length === 0
                      ? <span className="text-gray-400 text-xs">Sem dias nesta semana.</span>
                      : diasSem.map((d) => <LinhaDiaPonto key={d.data} d={d} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
